(() => {
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `£${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const parseVariants = (root) => {
    const script = root.querySelector('[data-variants-json]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Splay27: Unable to read product variants.', error);
      return [];
    }
  };

  const initGallery = (root) => {
    const showMedia = (mediaId) => {
      root.querySelectorAll('[data-media-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', String(panel.dataset.mediaPanel) === String(mediaId));
      });
      root.querySelectorAll('[data-media-thumb]').forEach((button) => {
        button.setAttribute(
          'aria-current',
          String(button.dataset.mediaThumb) === String(mediaId) ? 'true' : 'false'
        );
      });
    };

    root.querySelectorAll('[data-media-thumb]').forEach((button) => {
      button.addEventListener('click', () => showMedia(button.dataset.mediaThumb));
    });

    return showMedia;
  };

  /* ── Thumbnail scroll arrows ── */
  const initThumbScroll = (root) => {
    const thumbList = root.querySelector('[data-thumb-list]');
    const prevBtn   = root.querySelector('[data-thumb-prev]');
    const nextBtn   = root.querySelector('[data-thumb-next]');
    if (!thumbList || (!prevBtn && !nextBtn)) return;

    const THUMB_STEP = 88 + 12; // height + gap

    const updateArrows = () => {
      if (prevBtn) prevBtn.disabled = thumbList.scrollTop <= 0;
      if (nextBtn) nextBtn.disabled = thumbList.scrollTop >= thumbList.scrollHeight - thumbList.clientHeight - 1;
    };

    if (prevBtn) prevBtn.addEventListener('click', () => {
      thumbList.scrollBy({ top: -THUMB_STEP, behavior: 'smooth' });
      setTimeout(updateArrows, 350);
    });
    if (nextBtn) nextBtn.addEventListener('click', () => {
      thumbList.scrollBy({ top: THUMB_STEP, behavior: 'smooth' });
      setTimeout(updateArrows, 350);
    });

    updateArrows();
  };

  /* ── Mobile swipe on main image stage ── */
  const initSwipe = (root, showMedia) => {
    const stage = root.querySelector('[data-media-stage]');
    if (!stage) return;

    let startX = 0;
    let startY = 0;

    stage.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    stage.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      // Ignore vertical swipes and swipes shorter than 40px
      if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;

      const panels = [...root.querySelectorAll('[data-media-panel]')];
      const activeIdx = panels.findIndex(p => p.classList.contains('is-active'));
      if (activeIdx === -1) return;

      let nextIdx = activeIdx;
      if (dx < 0) nextIdx = Math.min(panels.length - 1, activeIdx + 1); // swipe left → next
      else        nextIdx = Math.max(0, activeIdx - 1);                  // swipe right → prev

      if (nextIdx !== activeIdx) {
        showMedia(panels[nextIdx].dataset.mediaPanel);
      }
    }, { passive: true });
  };

  const initQuantity = (root) => {
    root.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.closest('.splay27-quantity');
        const input = group?.querySelector('input[name="quantity"]');
        if (!input) return;
        const current = Math.max(1, Number(input.value) || 1);
        input.value = button.hasAttribute('data-qty-minus') ? Math.max(1, current - 1) : current + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initVariants = (root, showMedia) => {
    const variants = parseVariants(root);
    const optionSelects = [...root.querySelectorAll('[data-option-select]')];
    if (!variants.length || !optionSelects.length) return;

    const variantIdInput = root.querySelector('[data-variant-id]');
    const price          = root.querySelector('[data-price]');
    const comparePrice   = root.querySelector('[data-compare-price]');
    const sku            = root.querySelector('[data-sku]');
    const stock          = root.querySelector('[data-stock]');
    const addButton      = root.querySelector('[data-add-button]');
    const addLabel       = root.querySelector('[data-add-label]');
    const currency       = root.dataset.currency || 'GBP';
    const customKit      = root.dataset.productMode === 'custom-kit';
    const bundleMissing  = root.dataset.bundleMissing === 'true';

    const selectedVariant = () => {
      const selectedOptions = optionSelects.map((select) => select.value);
      return variants.find((variant) =>
        variant.options.every((option, index) => option === selectedOptions[index])
      );
    };

    const update = () => {
      const variant = selectedVariant();
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price, currency);
      if (comparePrice) {
        comparePrice.textContent = variant.compare_at_price && variant.compare_at_price > variant.price
          ? formatMoney(variant.compare_at_price, currency)
          : '';
      }
      if (sku)   sku.textContent   = variant.sku ? `SKU: ${variant.sku}` : '';
      if (stock) {
        stock.textContent = variant.available ? 'In stock' : 'Out of stock';
        stock.classList.toggle('is-in-stock', Boolean(variant.available));
      }
      if (addButton) addButton.disabled = !variant.available || bundleMissing;
      if (addLabel) {
        if (bundleMissing)        addLabel.textContent = 'Bundle selector not configured';
        else if (!variant.available) addLabel.textContent = 'Out of stock';
        else addLabel.textContent = customKit ? 'Add Custom Kit to Basket' : 'Add to Basket';
      }
      if (variant.featured_media?.id) showMedia(variant.featured_media.id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    optionSelects.forEach((select) => select.addEventListener('change', update));
    // Sync gallery with the initially selected variant (handles ?variant= URL param from collection cards)
    update();
  };

  const initProduct = (root) => {
    if (!root || root.dataset.splay27Ready === 'true') return;
    root.dataset.splay27Ready = 'true';
    const showMedia = initGallery(root);
    initThumbScroll(root);
    initSwipe(root, showMedia);
    initQuantity(root);
    initVariants(root, showMedia);
  };

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-splay27-product]').forEach(initProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();